import React from 'react'
import styled from '~/app/components/styled'
import { pushState } from '~/app/components/Router'
import { Icon_arrowmore_fill } from '@yanolja-rnd/ya-com-system-icon'
import { Typography } from '@yanolja-rnd/ya-com-core'

const Required = styled.span`
  &:after {
    margin-left: 0.2rem;
    content: '*';
    font-weight: normal;
    color: ${p => p.theme.color.yared};
  }
`

const Wrapper = styled.div<{
  hasBottomContent: boolean
  backgroundTransparent?: boolean
  isAvailableTransition: boolean
}>`
  padding-top: 0.4rem;
  padding-bottom: ${({ hasBottomContent }) => (hasBottomContent ? '0' : '.4rem')};
  ${({ backgroundTransparent, theme }) =>
    backgroundTransparent
      ? 'background-color: transparent'
      : `background-color: ${theme.color.white}`};
  ${({ isAvailableTransition }) => isAvailableTransition && 'transition: 0.2s'};
`

const Container = styled.div<{ type: 'small' | 'large'; isUsePadding: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${({ isUsePadding }) => (isUsePadding ? '0 2rem' : '0')};
  height: ${({ type }) => (type === 'small' ? '4.8rem' : '5.6rem')};
`

/* [Design system 의 스펙]
 * title 과 subtitle 가 string 일 경우 한줄 유지
 * 넘어갈시 그냥 잘라버림.
 * (...) 말줄임표도 노출 x -> text-overflow: ellipse 및 line-clamp 사용 불가
 * 단어도 최소 한 글자 단위로 잘려야 함 -> overflow: hidden 사용 불가
 * 따라서, 타이틀 font 의 line-height 만큼의 고정 height 를 두고 overflow-y: hidden 으로 구현
 * */
const Title = styled.div<{ isText: boolean }>`
  ${({ isText }) => isText && 'height: 1.9rem'};
  ${Typography('subtitle3').bold};
  overflow-y: hidden;
  color: ${({ theme }) => theme.color.gray900};
`

const SubTitle = styled.div<{ isText: boolean }>`
  ${({ isText }) => isText && 'height: 1.5rem'};
  overflow-y: hidden;
  margin-top: 0.2rem;
  ${Typography('body2').regular};
  color: ${({ theme }) => theme.color.gray600};
`

const InfoText = styled.span`
  ${Typography('body2').regular};
  white-space: nowrap;
  margin-left: 2rem;
  color: ${({ theme }) => theme.color.blue600};
`

const Button = styled.button`
  ${Typography('body1').bold};
  white-space: nowrap;
  margin-left: 2rem;
  padding: 0.8rem 0;
  color: ${p => p.theme.color.blue600};

  :hover,
  :active {
    background-color: ${({ theme }) => `${theme.color.blue600}19`}; // blue600 with opacity 10%
  }
`

const IconButtonContainer = styled.button`
  position: relative;
  padding: 0 1.2rem;
  left: 1.2rem;
  margin-left: 2rem;
  height: 4rem;
  display: flex;
  justify-content: center;
  align-items: center;
`

const FoldIcon = styled(Icon_arrowmore_fill)<{ open: boolean }>`
  width: 1.6rem;
  height: 1.6rem;
  transition: transform 0.2s ease;
  ${({ open }) => open && `transform: rotate(180deg)`};
`

export const SubHeaderInfoText = (props: Omit<React.ComponentProps<'button'>, 'type'>) => {
  return <InfoText {...props} />
}

export const SubHeaderButton = ({
  href,
  ...props
}: Omit<React.ComponentProps<'button'>, 'type'> & { href?: string }) => {
  const handleHref = (e: React.MouseEvent) => {
    if (href) {
      e.preventDefault()
      pushState(href)
    }
  }

  return <Button onClick={href ? handleHref : props.onClick}>{props.children}</Button>
}

export const SubHeaderFoldButton = ({
  isOpen,
  ...rest
}: Omit<React.ComponentProps<'button'>, 'type'> & { isOpen: boolean }) => {
  return (
    <IconButtonContainer {...rest}>
      <FoldIcon open={isOpen} />
    </IconButtonContainer>
  )
}

type Props = {
  title: React.ReactNode
  subTitle?: React.ReactNode
  right?: React.ReactNode
  required?: boolean
  onTitleClick?: () => void
  hasBottomContent?: boolean
  backgroundTransparent?: boolean
  isAvailableTransition?: boolean
  isUsePadding?: boolean
} & Omit<React.ComponentProps<'div'>, 'title'>

/*
 * [SubHeader: Design System Component]
 *
 * Design system component 의 경우 strict 하게 style 을 적용하는게 원칙이지만,
 * SubHeader component 의 경우 design system 에서 정의한 케이스 외의
 * '변형 케이스' 도 지원을 하기 위해 title, subTitle, right prop 을 React.ReactNode type 으로 받는다.
 *
 * '변형 케이스': 디자인 시스템은 아님. 디자인시스템 형태와 유사하게 디자인된 케이스
 *
 * ---------
 * | Props |
 * ---------
 *
 * title  타이틀. 단순 string 혹은 ReactNode 타입으로 넘겨 변형 케이스 구현 가능. (예: 타이틀 옆에 checkbox 추가 가능)
 * subTitle: 서브타이틀. 서브 타이틀의 유무가 subheader 의 large, small type 을 결정짓는다.
 * right: 서브헤더 오른쪽에 위치할 컴포넌트. 해당 컴포넌트에선 디자인시스템에서 정의한 케이스의 컴포넌트를 미리 구현하여 제공한다.
 *    SubHeaderInfoText: 인포텍스트
 *    SubHeaderButton: 텍스트버튼
 *    SubHeaderFoldButton: 아이콘버튼 (다른 아이콘으로 변경 원할시 컴포넌트를 별도로 작성 필요)
 * required: 타이틀 옆에 붉은 * 마크 (필수 마크)
 * onTitleClick: 타이틀 영역 클릭 이벤트 핸들러
 * hasBottomContent: 현재 디자인시스템 스펙에선 48px, 56px 의 높이의 서브헤더 컨텐츠에서 추가로 위아래로 4px 씩 padding 을 제공한다.
 *                   이때 서브헤더 바로 아래에 다른 컨텐츠가 인접해 있다면 아래 4px 의 padding 을 제공하지 않는다.
 *                   해당 prop 은 이를 구별하는 flag
 * backgroundTransparent: 배경 투명 유무 flag.
 * isAvailableTransition: Fold Component 사용시 Content animation 사용 여부 flag.
 * */

const SubHeader = ({
  title,
  subTitle,
  right,
  required,
  onTitleClick,
  hasBottomContent = false,
  backgroundTransparent,
  isAvailableTransition = false,
  isUsePadding = true,
}: Props) => {
  return (
    <Wrapper
      hasBottomContent={hasBottomContent}
      backgroundTransparent={backgroundTransparent}
      isAvailableTransition={isAvailableTransition}
    >
      <Container type={subTitle ? 'large' : 'small'} isUsePadding={isUsePadding}>
        <div>
          <Title isText={typeof title === 'string'} onClick={onTitleClick}>
            {required ? <Required>{title}</Required> : title}
          </Title>
          {subTitle && <SubTitle isText={typeof subTitle === 'string'}>{subTitle}</SubTitle>}
        </div>
        {right}
      </Container>
    </Wrapper>
  )
}

export default SubHeader
